import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useTexture } from '@react-three/drei';
import * as THREE from 'three';

export default function ShirtModel({ frontImage, backImage, color = '#ffffff', onLoad }) {
    const groupRef = useRef();

    // Load textures
    const [front, back] = useTexture([
        frontImage || '/logo.png',
        backImage || '/logo.png'
    ]);

    // Notify parent when textures are loaded
    useEffect(() => {
        if (front && back && onLoad) {
            onLoad();
        }
    }, [front, back, onLoad]);

    // Auto-rotation
    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * 0.5;
        }
    });

    // Define the T-Shirt Shape
    const tshirtShape = useMemo(() => {
        const shape = new THREE.Shape();

        // Starting at bottom center
        shape.moveTo(0, -2);

        // Right side
        shape.lineTo(1.2, -2); // Bottom right corner
        shape.lineTo(1.2, 0.5); // Armpit
        shape.lineTo(2.0, 0.2); // Sleeve bottom
        shape.lineTo(2.2, 1.0); // Sleeve edge
        shape.lineTo(1.0, 1.8); // Shoulder
        shape.quadraticCurveTo(0.5, 1.8, 0.4, 1.6); // Neck right
        shape.quadraticCurveTo(0, 1.3, -0.4, 1.6); // Neck curve to left

        // Left side (Mirror of right)
        shape.quadraticCurveTo(-0.5, 1.8, -1.0, 1.8); // Shoulder left
        shape.lineTo(-2.2, 1.0); // Sleeve edge left
        shape.lineTo(-2.0, 0.2); // Sleeve bottom left
        shape.lineTo(-1.2, 0.5); // Armpit left
        shape.lineTo(-1.2, -2); // Bottom left corner

        shape.lineTo(0, -2); // Close loop

        return shape;
    }, []);

    const extrudeSettings = {
        depth: 0.4, // Thickness of the shirt
        bevelEnabled: true,
        bevelThickness: 0.1,
        bevelSize: 0.1,
        bevelSegments: 5
    };

    return (
        <group ref={groupRef}>
            {/* Extruded T-Shirt Shape */}
            <mesh position={[0, 0, -0.2]}> {/* Center the extrusion depth */}
                <extrudeGeometry args={[tshirtShape, extrudeSettings]} />
                <meshStandardMaterial
                    color={color}
                    roughness={0.4}
                    metalness={0.1}
                />
            </mesh>

            {/* Front Design */}
            <mesh position={[0, 0.2, 0.32]}>
                <planeGeometry args={[1.5, 1.8]} />
                <meshStandardMaterial
                    map={front}
                    transparent
                    opacity={1}
                    roughness={0.8}
                    side={THREE.DoubleSide}
                />
            </mesh>

            {/* Back Design */}
            <mesh position={backImage?.includes('navajitas') ? [0, -0.4, -0.32] : [0, -0.2, -0.32]} rotation={[0, Math.PI, 0]}>
                <planeGeometry args={backImage?.includes('navajitas') ? [1.8, 2.9] : [1.8, 2.5]} />
                <meshStandardMaterial
                    map={back}
                    transparent
                    opacity={1}
                    roughness={0.8}
                    side={THREE.DoubleSide}
                />
            </mesh>
        </group>
    );
}
